import { type ReactNode, useCallback, useId } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { IconBookmark } from '@tabler/icons-react';

import type { SavedSearchFieldsFragment } from './__generated__/saved-searches.zeus';
import { SavedSearchesMenu } from './SavedSearchesMenu';
import { useSavedSearchesMenu } from './useSavedSearchesMenu';

export interface SavedSearchesSectionProps {
  /** Applies the selected saved search. The host page owns the search state. */
  onApplySavedSearch: (savedSearch: SavedSearchFieldsFragment) => void;
  /** The saved search currently applied on the page; its row gets an "Active" marker. */
  activeSavedSearchId?: string | null;
  /** Save or update action for the current search, shown under the list. */
  saveSearchControl?: ReactNode;
  /** Sends a signed-out visitor to log in. */
  onLogin: () => void;
  /** Opens the filters. Backs the empty state's call to action. */
  onOpenFilters?: () => void;
  /** Takes focus from a removed saved search as it leaves; see `SavedSearchesMenu`. */
  onRemovedWithFocus?: () => void;
}

/**
 * Saved searches as a section of a larger list, such as the location search
 * suggestions. Signed-out visitors get a login row so the feature stays
 * discoverable.
 */
export const SavedSearchesSection = ({
  onApplySavedSearch,
  activeSavedSearchId,
  saveSearchControl,
  onLogin,
  onOpenFilters,
  onRemovedWithFocus,
}: SavedSearchesSectionProps) => {
  const headingId = useId();
  const { isLoggedIn, savedSearches, rename, confirmDelete } = useSavedSearchesMenu();
  const removeSavedSearch = useCallback(
    (savedSearch: SavedSearchFieldsFragment) => confirmDelete(savedSearch.id),
    [confirmDelete],
  );

  return (
    <Col as="section" aria-labelledby={headingId} className="gap-3 px-3 pt-3 pb-2">
      <Text as="p" id={headingId} className="text-label-md text-od-content-primary">
        Saved searches
      </Text>
      {isLoggedIn ? (
        <>
          <SavedSearchesMenu
            layout="cards"
            savedSearches={savedSearches}
            activeSavedSearchId={activeSavedSearchId}
            onSelect={onApplySavedSearch}
            onRename={rename}
            onRemove={removeSavedSearch}
            onRemovedWithFocus={onRemovedWithFocus}
            onOpenFilters={onOpenFilters}
          />
          {saveSearchControl ? (
            <Col className="items-stretch gap-2 pt-3 text-center">{saveSearchControl}</Col>
          ) : null}
        </>
      ) : (
        <Box
          aria-label="Log in to see saved searches"
          analyticsName="saved-searches-section-login"
          onClick={onLogin}
          className="w-full items-center justify-start gap-3 rounded-2xl border border-od-border-primary p-4 text-left transition-colors hover:bg-od-bg-secondary"
        >
          <IconBookmark
            aria-hidden
            size={20}
            className="shrink-0 text-od-content-inactive-primary"
          />
          <Text as="span" className="text-body-sm leading-5">
            Log in to see saved searches
          </Text>
        </Box>
      )}
    </Col>
  );
};
